
import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, Menu, LogOut, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { usePassword } from '@/contexts';
import debounce from 'lodash/debounce';
import { toast } from 'sonner';
import { useStudents } from '@/contexts';
import { useAdminData } from '@/hooks/useAdminData';
import { useTeacherData } from '@/hooks/useTeacherData';
import { defaultToastConfig } from '@/utils/toastConfig';

type HeaderProps = {
  isMobile: boolean;
  toggleSidebar: () => void;
};

const Header: React.FC<HeaderProps> = ({
  isMobile,
  toggleSidebar
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { clearAuthSession } = usePassword();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Get data refresh functions from various contexts
  const { refreshStudents } = useStudents();
  const { refreshAdmins } = useAdminData();
  const { refreshTeachers } = useTeacherData();

  // Memoize the debounced search function
  const debouncedSearch = useMemo(
    () =>
      debounce((query: string) => {
        if (!query.trim()) {
          setSearchError('Please enter a search query');
          return;
        }
        setSearchError(null);
        setIsSearching(true);
        // Redirecting to the universal search page with the query
        navigate(`/search?q=${encodeURIComponent(query.trim())}`);
        setTimeout(() => setIsSearching(false), 500);
      }, 300),
    [navigate]
  );

  const handleSearch = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    debouncedSearch(searchQuery);
  }, [searchQuery, debouncedSearch]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setSearchError(null);
  }, []);

  const handleSignOut = useCallback(() => {
    // Clear the auth session and reload to show the password protection screen
    clearAuthSession();
    window.location.reload();
  }, [clearAuthSession]);

  // Function to refresh all application data
  const handleRefresh = async () => {
    if (isRefreshing) return; // Prevent multiple simultaneous refreshes
    
    setIsRefreshing(true);
    toast.info('Refreshing application data...', { 
      duration: defaultToastConfig.duration 
    });
    
    try {
      // Refresh students data
      try {
        await refreshStudents();
      } catch (error) {
        console.error('Error refreshing students data:', error);
      }
      
      // Refresh admins data
      try {
        await refreshAdmins();
      } catch (error) {
        console.error('Error refreshing admins data:', error);
      }
      
      // Refresh teachers data
      try {
        await refreshTeachers();
      } catch (error) {
        console.error('Error refreshing teachers data:', error);
      }
      
      // Additional refresh calls can be added here as needed
      
      toast.success('Application data refreshed successfully', {
        duration: defaultToastConfig.duration
      });
    } catch (error) {
      console.error('Error during application refresh:', error);
      toast.error('Failed to refresh some application data', {
        duration: defaultToastConfig.duration
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-2 sm:p-4 border-b bg-white dark:bg-slate-900 space-y-2 sm:space-y-0 w-full sticky top-0 z-40">
      <div className="flex items-center w-full sm:w-auto">
        {isMobile && (
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={toggleSidebar} 
            className="mr-2 h-8 w-8" 
            aria-label="Toggle menu"
          >
            <Menu className="h-4 w-4" />
          </Button>
        )}
        
        <div className="flex-1 w-full sm:max-w-md">
          <form onSubmit={handleSearch} className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input 
              placeholder="Search students, teachers, admins..." 
              className={`pl-7 h-8 text-sm bg-slate-100 dark:bg-slate-800 border-0 rounded-full ${searchError ? 'border-red-500 focus:border-red-500' : ''}`}
              value={searchQuery} 
              onChange={handleSearchChange}
              disabled={isSearching}
              aria-label="Universal search"
            />
            {searchError && (
              <div className="absolute text-xs text-red-500 mt-1">{searchError}</div>
            )}
          </form>
        </div>
      </div>
      
      <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
        {/* Refresh button */}
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="h-8 w-8 rounded-full hover:bg-blue-100 dark:hover:bg-blue-900/20 transition-colors"
          aria-label="Refresh application data"
          title="Refresh application data"
        >
          <RefreshCw className={`h-4 w-4 text-blue-500 dark:text-blue-400 ${isRefreshing ? 'animate-spin' : ''}`} />
        </Button>
        
        <NotificationBell />
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={handleSignOut}
          className="h-8 w-8 rounded-full hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors"
          aria-label="Sign out"
          title="Sign out"
        >
          <LogOut className="h-4 w-4 text-red-500 dark:text-red-400" />
        </Button>
      </div>
    </header>
  );
};

export default React.memo(Header);
